import React, { MutableRefObject, useContext, useMemo, useRef } from 'react';
import cx from 'classnames';
import RecorderDrawerContext from '../../Context';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { getFullDateString } from '~/utils/date';
import {
  AnimatedDiv,
  HideAnimation,
  ShowAnimation,
} from '@vimily/bonjoro-ui-components-v2/AnimatedDiv';
import { DateTime } from 'luxon';
import { Switch } from '@vimily/bonjoro-ui-components-v2/Switch';
import { GreetSendType } from '~/types/models';
import { ShowTrigger, Tooltip } from '@vimily/bonjoro-ui-components-v2/Tooltip';
import { useOptimisedSending } from '~/react-hooks/use-optimised-sending';
import { useFeatures } from '~/react-hooks/use-features';

const defaultButtonClass = 'font-bold w-full text-[21px] leading-6';

export function SendingPanel() {
  const {
    ctaText,
    profiles,
    setSchedulerModalOpen,
    sendAt,
    sendType,
    setSendAt,
    setSendType,
    onSubmit,
    closeDrawer,
    isUploading,
    onScheduleRemove,
    onScheduleLater,
    onChangeScheduleClick,
    hideScheduler,
    disableCta,
  } = useContext(RecorderDrawerContext);
  const { canSchedule: canAccessScheduling } = useFeatures();
  const { setValue: setOptimisedSending } = useOptimisedSending();

  const tooltipContainerRef = useRef<HTMLSpanElement>(null);

  const isOptimisedSending = sendType == GreetSendType.Optimised;
  const canSchedule =
    profiles != undefined && profiles.length == 1 && canAccessScheduling;

  const onOptimisedSendingToggle = (value: boolean) => {
    setSendType(value ? GreetSendType.Optimised : GreetSendType.Manual);
    setOptimisedSending(value);
  };

  const onSubmitHandler = async () => {
    await onSubmit?.();
    closeDrawer();
  };

  const onScheduleLaterClick = () => {
    setSchedulerModalOpen(true);

    if (sendAt) {
      onChangeScheduleClick?.();
    } else {
      onScheduleLater?.();
    }
  };

  const onRemoveSchedule = () => {
    if (!isUploading) {
      setSendAt(undefined);
      onScheduleRemove?.();
    }
  };

  const sendMessageCtaLabel = useMemo(() => {
    if (sendAt) {
      return 'Send scheduled message';
    } else if (isOptimisedSending) {
      return 'Send message';
    }

    return ctaText ?? 'Send message now';
  }, [sendAt, isOptimisedSending, ctaText]);

  const isCtaDisabled = disableCta || isUploading;

  return (
    <div className='w-full flex flex-col gap-y-4 p-4'>
      {!hideScheduler && (
        <AnimatedDiv
          show={sendAt != undefined}
          className='flex flex-col gap-x-4 justify-between'
          showAnimation={ShowAnimation.SlideInDown150}
          hideAnimation={HideAnimation.SlideOutUp150}
        >
          <div className='flex flex-row justify-between items-center'>
            <div className='flex flex-row gap-x-2 items-center'>
              <i className='icon icon-scheduled !v2-bg-neutral-900 w-10 h-10' />
              <span className='text-[21px] font-bold !v2-bg-text-900'>
                Scheduled
              </span>
            </div>
            <FontAwesomeIcon
              icon={faTrash}
              className={cx('v2-text-neutral-300 cursor-pointer', {
                'hover:v2-text-orange-700': !isUploading,
                '!cursor-not-allowed': isUploading,
              })}
              onClick={onRemoveSchedule}
            />
          </div>
          <span className='text-base truncate'>
            {getFullDateString(sendAt ?? DateTime.local())}
          </span>
        </AnimatedDiv>
      )}
      <Button
        className={defaultButtonClass}
        variant={ButtonVariant.Primary}
        onClick={() => void onSubmitHandler()}
        disabled={isCtaDisabled}
      >
        {sendMessageCtaLabel}
      </Button>
      {!hideScheduler && (
        <>
          <AnimatedDiv
            show={canSchedule && !isOptimisedSending}
            showAnimation={ShowAnimation.SlideInLeft}
            hideAnimation={HideAnimation.SlideOutRight}
          >
            <Button
              className={defaultButtonClass}
              variant={ButtonVariant.Secondary}
              onClick={onScheduleLaterClick}
              disabled={isCtaDisabled}
            >
              {sendAt ? 'Change schedule' : 'Schedule to send later'}
            </Button>
          </AnimatedDiv>
          <AnimatedDiv
            show={canSchedule && sendAt == undefined}
            className='w-full flex flex-row justify-between'
            showAnimation={ShowAnimation.SlideInUp150}
            hideAnimation={HideAnimation.SlideOutDown150}
          >
            <span
              className='flex flex-row gap-x-3 items-center'
              ref={tooltipContainerRef}
            >
              <i className='icon icon-optimised-sending !v2-bg-neutral-900' />
              <span className='font-bold text-[21px] v2-text-neutral-900'>
                Use optimised sending
              </span>
              <Tooltip
                parentRef={tooltipContainerRef as MutableRefObject<HTMLElement>}
                align='center'
                reposition
                className='!z-[999]'
                bubbleClassName={'!line-clamp-6 !p-2.5'}
                arrowSize={7.5}
                maxWidth={250}
                maxHeight={185}
                showTrigger={ShowTrigger.Click}
                content={
                  <span className='text-base'>
                    Optimised sending aims to deliver your message to your
                    recipient’s inbox at the best time to get your video message
                    opened and watched. Check out our &nbsp;
                    <a
                      href={`${window.Bonjoro.faqUrl}/en/articles/5696833-optimized-sending`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className={cx([
                        'hover:v2-text-white hover:v2-underline focus:v2-text-white',
                        'underline line-height-5 v2-text-white ',
                      ])}
                    >
                      help center here
                    </a>
                  </span>
                }
              >
                <FontAwesomeIcon
                  className='text-xl v2-text-neutral-900 cursor-pointer'
                  icon={faQuestionCircle}
                />
              </Tooltip>
            </span>
            <Switch
              bodyClassName='w-10 h-5'
              value={sendType === GreetSendType.Optimised}
              onToggle={onOptimisedSendingToggle}
              disabled={isUploading}
            />
          </AnimatedDiv>
        </>
      )}
    </div>
  );
}
